/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResult } from '@app/app/shared/store';

function nonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * The explanation carried in a failed request's body, or null when there is none worth showing.
 *
 * Reads the API's `ErrorMessage` (`{ message }`), its 422 `ValidationErrorMessage` (`{ errors: { field: message } }`,
 * joined into one sentence), a plain-text body, and an `ErrorResult`'s `error`. A body starting with `<` is an HTML
 * page from a proxy or gateway rather than anything the API said, so it is dropped. Anything that is not a request
 * failure (a plain `Error` thrown client-side, say) has no body and yields null.
 */
export function serverErrorMessage(error: unknown): string | null {
  if (error instanceof ErrorResult) {
    return nonBlank(error.error) ? error.error : null;
  }
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  const body: unknown = error.error;
  if (typeof body === 'string') {
    return nonBlank(body) && !body.trimStart().startsWith('<') ? body : null;
  }
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const { message, errors } = body as { message?: unknown; errors?: unknown };
  if (nonBlank(message)) {
    return message;
  }
  if (typeof errors === 'object' && errors !== null) {
    const messages = Object.values(errors).filter(nonBlank);
    return messages.length ? messages.join(' ') : null;
  }
  return null;
}

/**
 * The text to show a user when a request fails: the server's own explanation for a 4xx, `fallback` for everything
 * else. A 4xx says what the user asked for that cannot be done, which they can act on ("Cannot publish an assignment
 * without tasks"). A 5xx or a network failure is ours to fix, and its body (a stack-trace summary, a gateway page)
 * gives them nothing to act on, so it gets the generic message. An `ErrorResult` has already been through a data
 * service that chose its message, so that is shown as well.
 */
export function userFacingRequestError(error: unknown, fallback: string): string {
  const clientError = error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
  if (clientError || error instanceof ErrorResult) {
    return serverErrorMessage(error) ?? fallback;
  }
  return fallback;
}

/**
 * Converts an error from a HTTP request to a string.
 */
export function requestErrorToString(error: unknown): string {
  return serverErrorMessage(error) ?? (error instanceof HttpErrorResponse ? error.message : 'unknown error');
}
